// ═══════════════════════════════════════════════════════════
// Release Tokens — per-request secure download links
//
// Reuses the uploadTokens table (label = 'release') to avoid
// adding a new GAB table. One token per request; created on the
// first document release and reused for all subsequent releases.
//
// Public portal uses /portal/release/:token to download files.
// ═══════════════════════════════════════════════════════════

import { queryRecords } from './client';
import { createUploadToken, type UploadToken } from './uploadTokens';
import { TABLES } from './tables';
import { FIELDS } from './fields';

const F = FIELDS.uploadTokens;

function rowToToken(row: Record<string, unknown>): UploadToken {
  return {
    recordId:  row['record_id'] as number,
    requestId: Number(row[F.requestId] ?? 0),
    token:     String(row[F.token] ?? ''),
    label:     String(row[F.label] ?? ''),
    expiresAt: String(row[F.expiresAt] ?? ''),
    isActive:  row[F.isActive] === true || row[F.isActive] === 'true',
    createdBy: String(row[F.createdBy] ?? ''),
    createdAt: String(row[F.createdAt] ?? ''),
  };
}

/**
 * Returns the existing release token for a request, or creates one.
 * isNew = true only when a token is freshly created — use this to
 * decide whether to send the "Records Ready" correspondence.
 */
export async function getOrCreateReleaseToken(
  requestId: number,
  actorEmail: string,
): Promise<{ token: UploadToken; isNew: boolean }> {
  const { records } = await queryRecords(TABLES.uploadTokens, {
    filters: [
      { field: F.requestId, operator: 'eq', value: requestId },
      { field: F.label,     operator: 'eq', value: 'release'  },
      { field: F.isActive,  operator: 'eq', value: true        },
    ],
    limit: 1,
  });

  if (records.length > 0) {
    return { token: rowToToken(records[0] as Record<string, unknown>), isNew: false };
  }

  const token = await createUploadToken({
    requestId,
    label:      'release',
    expiryDays: 365,
    createdBy:  actorEmail,
  });

  return { token, isNew: true };
}

/**
 * Validate a release token from the public portal.
 * Returns the token (including requestId) if valid, null otherwise.
 * Rejects tokens that are expired, inactive, or not label='release'.
 */
export async function validateReleaseToken(tokenStr: string): Promise<UploadToken | null> {
  const { records } = await queryRecords(TABLES.uploadTokens, {
    filters: [
      { field: F.token,    operator: 'eq', value: tokenStr  },
      { field: F.label,    operator: 'eq', value: 'release'  },
      { field: F.isActive, operator: 'eq', value: true       },
    ],
    limit: 1,
  }, { anonymous: true });

  if (!records.length) return null;

  const t = rowToToken(records[0] as Record<string, unknown>);
  if (new Date(t.expiresAt) < new Date()) return null;

  return t;
}

/** Build the public-facing release URL for embedding in correspondence. */
export function buildReleaseUrl(token: string): string {
  return `${window.location.origin}/portal/release/${token}`;
}
